import React from 'react';
import { TrendingUp, Users, DollarSign, Shield, Award, Globe } from 'lucide-react';
import teamCelebrationImage from '../assets/team-happy-businesswomen-working-office-looking-camera_637285-13049.jpg_urq8qz.jpg';
import professionalManImage from '../assets/african-man-black-suit_1157-45575.jpg_rbjcix.jpg';

const Stats = () => {
  const stats = [
    {
      icon: DollarSign,
      number: '$4.2B+',
      label: 'Assets Under Management',
      color: 'text-green-500'
    },
    {
      icon: Users,
      number: '40K+',
      label: 'Active Clients',
      color: 'text-blue-500'
    },
    {
      icon: Award,
      number: '12+',
      label: 'Years Experience',
      color: 'text-gold-500'
    },
    {
      icon: Globe,
      number: '50+',
      label: 'Global Markets',
      color: 'text-red-500'
    },
    {
      icon: Users,
      number: '100+',
      label: 'Expert Agents',
      color: 'text-purple-500'
    },
    {
      icon: Shield,
      number: '99.9%',
      label: 'Security Rating',
      color: 'text-indigo-500'
    }
  ];

  return (
    <section className="py-16 bg-gradient-to-br from-gold-50 via-amber-50 to-yellow-50 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-0 w-full h-full" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23f59e0b' fill-opacity='0.4' fill-rule='evenodd'%3E%3Cpath d='m0 40l40-40h-40v40zm40 0v-40h-40l40 40z'/%3E%3C/g%3E%3C/svg%3E")`,
          backgroundSize: '40px 40px'
        }}></div>
      </div>
      {/* Decorative Elements */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-16 right-20 w-20 h-20 bg-gold-400 rounded-full"></div>
        <div className="absolute bottom-24 left-16 w-16 h-16 bg-amber-400 rounded-full"></div>
        <div className="absolute top-1/3 left-1/4 w-12 h-12 border-2 border-gold-400 rounded-full"></div>
      </div>
      <div className="relative z-10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold font-serif text-navy-900 mb-4">
            Our Performance
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Our track record speaks for itself through consistent results and client satisfaction.
          </p>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 items-center">
          
          {/* Left Image */}
          <div className="relative">
            <div className="relative rounded-2xl overflow-hidden shadow-xl transform hover:scale-105 transition-transform duration-500">
              <img
                src={teamCelebrationImage}
                alt="Happy business team celebrating success"
                className="w-full h-80 object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy-900/60 to-transparent"></div>
              
              {/* Overlay Content */}
              <div className="absolute bottom-6 left-6 right-6">
                <div className="bg-white/95 backdrop-blur-sm rounded-lg p-4">
                  <h5 className="font-bold text-navy-900 mb-1">Client Success Stories</h5>
                  <p className="text-sm text-gray-600">Celebrating every milestone together</p>
                </div>
              </div>
            </div>
          </div>

          {/* Center Stats */}
          <div className="text-center">
            <div className="grid grid-cols-2 gap-6">
              {stats.map((stat, index) => {
                const IconComponent = stat.icon;
                return (
                  <div 
                    key={index} 
                    className="group animate-fade-in"
                    style={{animationDelay: `${index * 100}ms`}}
                  >
                    <div className="flex flex-col items-center space-y-2">
                      <IconComponent className={`h-6 w-6 ${stat.color} group-hover:scale-110 transition-transform duration-300`} />
                      <div className={`text-2xl md:text-3xl font-bold ${stat.color} animate-glow-pulse`}>
                        {stat.number}
                      </div>
                      <div className="text-sm text-gray-600 font-medium text-center leading-tight">
                        {stat.label}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            
            {/* Achievement Badge */}
            <div className="mt-8 inline-flex items-center px-6 py-3 bg-gradient-to-r from-gold-500 to-gold-600 text-white rounded-full shadow-lg animate-bounce-in animation-delay-500">
              <Award className="h-5 w-5 mr-2" />
              <span className="font-semibold">Trusted by Thousands</span>
            </div>
          </div>

          {/* Right Image */}
          <div className="relative">
            <div className="relative rounded-2xl overflow-hidden shadow-xl transform hover:scale-105 transition-transform duration-500">
              <img
                src={professionalManImage}
                alt="Professional financial advisor"
                className="w-full h-80 object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy-900/60 to-transparent"></div>
              
              {/* Overlay Badge */}
              <div className="absolute top-6 right-6">
                <div className="bg-gold-500 text-white px-4 py-2 rounded-full text-sm font-semibold shadow-lg">
                  Expert Leadership
                </div>
              </div>

              {/* Overlay Content */}
              <div className="absolute bottom-6 left-6 right-6">
                <div className="bg-white/95 backdrop-blur-sm rounded-lg p-4">
                  <h5 className="font-bold text-navy-900 mb-1">Professional Excellence</h5>
                  <p className="text-sm text-gray-600">Industry-leading expertise</p>
                </div>
              </div>
            </div>
          </div>

        </div>

        {/* Bottom Call to Action */}
        <div className="text-center mt-16">
          <div className="bg-gradient-to-r from-navy-900 to-navy-800 rounded-2xl p-8 md:p-12 relative overflow-hidden shadow-xl">
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-0 left-0 w-32 h-32 bg-gold-500 rounded-full -translate-x-16 -translate-y-16"></div>
              <div className="absolute bottom-0 right-0 w-48 h-48 bg-gold-500 rounded-full translate-x-24 translate-y-24"></div>
              <div className="absolute top-1/2 left-1/2 w-24 h-24 border-2 border-gold-400 rounded-full opacity-30 -translate-x-12 -translate-y-12"></div>
            </div>
            
            <div className="relative z-10">
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-4 font-serif animate-glow-pulse">
                Ready to be part of our success story?
              </h3>
              <p className="text-gray-300 text-lg mb-8 max-w-2xl mx-auto leading-relaxed">
                Join thousands of investors who trust us with their financial future. 
                Start your investment journey today with expert guidance and proven strategies.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <a 
                  href="/contact"
                  className="group inline-flex items-center px-8 py-4 bg-gradient-to-r from-gold-500 to-gold-600 text-white font-semibold rounded-full shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 hover:from-gold-600 hover:to-gold-700"
                >
                  <span>Start Your Journey</span>
                  <svg className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </a>
                
                <a 
                  href="/about"
                  className="inline-flex items-center px-6 py-3 border-2 border-white/30 text-white font-medium rounded-full hover:bg-white/10 hover:border-white/50 transition-all duration-300"
                >
                  Learn More About Us
                </a>
              </div>
              
              {/* Trust Indicators */}
              <div className="mt-8 flex flex-wrap justify-center items-center gap-6 text-gray-400 text-sm">
                <div className="flex items-center">
                  <Shield className="h-4 w-4 mr-2 text-gold-400" />
                  <span>Fully Regulated</span>
                </div>
                <div className="flex items-center">
                  <Award className="h-4 w-4 mr-2 text-gold-400" />
                  <span>12+ Years Experience</span>
                </div>
                <div className="flex items-center">
                  <Users className="h-4 w-4 mr-2 text-gold-400" />
                  <span>40K+ Happy Clients</span>
                </div>
              </div>
            </div>
          </div>
          </div>
        </div>
      </div>
      
      {/* Bottom Wave - Final Section */}
      <div className="absolute bottom-0 left-0 w-full overflow-hidden">
        <svg className="relative block w-full h-40" viewBox="0 0 1200 160" preserveAspectRatio="none">
          <defs>
            <linearGradient id="statsWaveGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" style={{stopColor: '#1e293b', stopOpacity: 1}} />
              <stop offset="25%" style={{stopColor: '#334155', stopOpacity: 1}} />
              <stop offset="50%" style={{stopColor: '#475569', stopOpacity: 1}} />
              <stop offset="75%" style={{stopColor: '#64748b', stopOpacity: 1}} />
              <stop offset="100%" style={{stopColor: '#94a3b8', stopOpacity: 1}} />
            </linearGradient>
            <linearGradient id="statsAccentGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" style={{stopColor: '#f59e0b', stopOpacity: 0.8}} />
              <stop offset="50%" style={{stopColor: '#d97706', stopOpacity: 0.6}} />
              <stop offset="100%" style={{stopColor: '#b45309', stopOpacity: 0.4}} />
            </linearGradient>
          </defs>
          
          {/* Main Wave */}
          <path 
            d="M0,20 C300,100 600,0 900,60 C1050,90 1150,40 1200,50 L1200,160 L0,160 Z" 
            fill="url(#statsWaveGradient)"
          />
          
          {/* Secondary Wave */}
          <path 
            d="M0,60 C250,10 500,120 750,70 C900,40 1100,100 1200,60 L1200,160 L0,160 Z" 
            fill="url(#statsWaveGradient)"
            opacity="0.8"
          />
          
          {/* Accent Wave */}
          <path 
            d="M0,100 C200,140 400,80 600,110 C800,140 1000,90 1200,100 L1200,160 L0,160 Z" 
            fill="url(#statsAccentGradient)"
          />
          
          {/* Final Wave */}
          <path 
            d="M0,130 C400,160 800,120 1200,140 L1200,160 L0,160 Z" 
            fill="url(#statsWaveGradient)"
            opacity="0.9"
          />
        </svg>
      </div>
    </section>
  );
};

export default Stats; 